import { uuid } from '@tvg/fuse-utils';
import PropTypes from 'prop-types';
import React from 'react';
import titleSizeConfig from './accordion-config';
import {
	StyledAccordion,
	StyledAccordionButton,
	StyledAccordionContent,
	StyledAccordionHeader,
	StyledAccordionHeading,
} from './styled-accordion';

const { heading: headingSizeConfig } = titleSizeConfig;

const { forwardRef, useCallback, useEffect, useMemo, useState } = React;

const propTypes = {
	chevronInline: PropTypes.bool,
	children: PropTypes.node,
	collapseCallback: PropTypes.func,
	dataTestId: PropTypes.string,
	expandCallback: PropTypes.func,
	heading: PropTypes.string,
	headingTag: PropTypes.string,
	hideBorder: PropTypes.bool,
	open: PropTypes.bool,
	titleSize: PropTypes.oneOfType([
		PropTypes.shape(
			['xxs', 'xs', 's', 'm', 'l', 'xl', 'xxl'].reduce(
				(acc, current) => ({
					...acc,
					[current]: PropTypes.oneOf(Object.keys(headingSizeConfig)),
				}),
				{},
			),
		),
	]),
	componentId: PropTypes.string,
};

const defaultProps = {
	chevronInline: false,
	children: null,
	collapseCallback: () => {},
	dataTestId: undefined,
	expandCallback: () => {},
	heading: '',
	headingTag: 'h3',
	hideBorder: false,
	open: false,
	titleSize: {
		xxs: 'xxsmall',
	},
	componentId: uuid(),
};

const Accordion = forwardRef(
	(
		{
			chevronInline,
			children,
			collapseCallback,
			dataTestId,
			expandCallback,
			heading,
			headingTag,
			hideBorder,
			open,
			titleSize,
			componentId,
			...props
		},
		ref,
	) => {
		const [isOpen, setIsOpen] = useState(false);
		const buttonId = useMemo(() => `fuse-accordion-button-${componentId}`, []);
		const contentId = useMemo(() => `fuse-accordion-content-${componentId}`, []);

		const headingSize = useMemo(
			() => ({
				// Map titleSize viewport sizes to heading typography element viewport sizes i.e. { xxs: 'medium', m: 'large', l: 'xlarge' } to { xxs: '4',  m: '5', l: '6' }
				xxs: headingSizeConfig[titleSize.xxs],
				xs: headingSizeConfig[titleSize.xs],
				s: headingSizeConfig[titleSize.s],
				m: headingSizeConfig[titleSize.m],
				l: headingSizeConfig[titleSize.l],
				xl: headingSizeConfig[titleSize.xl],
				xxl: headingSizeConfig[titleSize.xxl],
			}),
			[titleSize],
		);

		const handleClick = useCallback(
			(e) => {
				if (isOpen) {
					setIsOpen(false);
					collapseCallback(e);
				} else {
					setIsOpen(true);
					expandCallback(e);
				}
			},
			[isOpen],
		); // Toggle accordion to expand and collapse

		// Set the accordion to open or close based on the 'open' prop value
		useEffect(() => {
			if (open) {
				setIsOpen(true);
			} else {
				setIsOpen(false);
			}
		}, [open]);

		return (
			<StyledAccordion
				className={`${isOpen ? 'fuse-accordion__open' : 'fuse-accordion__closed'}`}
				data-testid={dataTestId || 'fuse-accordion'}
				hideBorder={hideBorder}
				ref={ref}
				{...props}
			>
				<StyledAccordionHeader as={headingTag}>
					<StyledAccordionButton
						aria-controls={contentId}
						aria-expanded={isOpen}
						onClick={handleClick}
						open={isOpen}
						type="button"
						id={buttonId}
						titleSize={titleSize}
					>
						<StyledAccordionHeading
							chevronInline={chevronInline}
							type="heading"
							tag="span"
							size={headingSize}
						>
							{heading}
						</StyledAccordionHeading>
					</StyledAccordionButton>
				</StyledAccordionHeader>
				<StyledAccordionContent
					aria-hidden={isOpen ? undefined : true}
					aria-labelledby={buttonId}
					className={`${isOpen ? '' : 'fuse-acccordion__content--hide'}`}
					id={contentId}
					role="region"
				>
					{children}
				</StyledAccordionContent>
			</StyledAccordion>
		);
	},
);

Accordion.propTypes = propTypes;
Accordion.defaultProps = defaultProps;

export { Accordion };

export default Accordion;
